import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Link, Stack, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import FeedXMedia from './FeedXMedia';

// Keep the app's accepted providers and identities; never embed an arbitrary URL.
export function parseFeedMedia(raw) {
  try {
    const url = new URL(raw.trim());
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password || url.port)
      return null;
    const host = url.hostname.toLowerCase().replace(/^www\./, '');
    const parts = url.pathname.split('/').filter(Boolean);
    if (['youtube.com', 'm.youtube.com', 'youtu.be', 'youtube-nocookie.com'].includes(host)) {
      const id =
        host === 'youtu.be'
          ? parts[0]
          : parts[0] === 'watch'
            ? url.searchParams.get('v')
            : ['embed', 'shorts', 'live'].includes(parts[0])
              ? parts[1]
              : null;
      if (!/^[\w-]{11}$/.test(id || '')) return null;
      const time = url.searchParams.get('start') || url.searchParams.get('t') || '';
      const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/.exec(time);
      const start = match
        ? Math.min(
            86400,
            Number(match[1] || 0) * 3600 + Number(match[2] || 0) * 60 + Number(match[3] || 0),
          )
        : 0;
      return {
        provider: 'YouTube',
        url: `https://www.youtube.com/watch?v=${id}`,
        embed: `https://www.youtube-nocookie.com/embed/${id}?playsinline=1&autoplay=0&start=${start}`,
      };
    }
    if (
      host === 'instagram.com' &&
      ['p', 'reel', 'tv'].includes(parts[0]) &&
      /^[\w-]{1,64}$/.test(parts[1] || '') &&
      parts.length === 2
    ) {
      const canonical = `https://www.instagram.com/${parts[0]}/${parts[1]}/`;
      return { provider: 'Instagram', url: canonical, embed: `${canonical}embed/` };
    }
    if (['x.com', 'twitter.com', 'mobile.twitter.com', 'mobile.x.com'].includes(host)) {
      const id =
        parts[0] === 'i' && parts[1] === 'web' && parts[2] === 'status'
          ? parts[3]
          : parts[1] === 'status'
            ? parts[2]
            : null;
      if (/^\d{1,25}$/.test(id || '') && /^\w+$/.test(parts[0])) {
        const path = parts[0] === 'i' ? `i/web/status/${id}` : `${parts[0]}/status/${id}`;
        return { provider: 'X', url: `https://x.com/${path}`, id };
      }
    }
  } catch {
    /* Unsupported links stay ordinary links. */
  }
  return null;
}

const safeWebUrl = (raw) => {
  if (!raw.trim()) return false;
  try {
    const url = new URL(raw, window.location.origin);
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password;
  } catch {
    return false;
  }
};

export function feedBlocks(html) {
  const fragment = DOMPurify.sanitize(html || '', {
    RETURN_DOM_FRAGMENT: true,
    ALLOWED_TAGS: [
      'p',
      'br',
      'span',
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'a',
      'img',
      'h1',
      'h2',
      'h3',
      'h4',
      'blockquote',
      'ul',
      'ol',
      'li',
      'pre',
      'code',
      'table',
      'tbody',
      'tr',
      'td',
      'th',
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'style', 'class', 'colspan', 'rowspan'],
  });
  fragment.querySelectorAll('*').forEach((node) => {
    const classes = Array.from(node.classList).filter((value) =>
      /^ql-(size-(small|large|huge)|align-(center|right|justify))$/.test(value),
    );
    node.removeAttribute('class');
    if (classes.length) node.setAttribute('class', classes.join(' '));
    // Preserve editorial typography, not layout/overlays or CSS network requests.
    const style = node.style;
    const retained = [
      'color',
      'background-color',
      'font-size',
      'font-weight',
      'font-style',
      'text-align',
      'text-decoration',
    ]
      .map((key) => [key, style.getPropertyValue(key)])
      .filter(([, value]) => value && !/url\s*\(|expression|var\s*\(/i.test(value));
    node.removeAttribute('style');
    retained.forEach(([key, value]) => node.style.setProperty(key, value));
    if (node.tagName === 'A') {
      if (!safeWebUrl(node.getAttribute('href') || '')) node.removeAttribute('href');
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
    if (node.tagName === 'IMG') {
      if (!safeWebUrl(node.getAttribute('src') || '')) node.remove();
      else {
        node.setAttribute('loading', 'lazy');
        node.setAttribute('referrerpolicy', 'no-referrer');
      }
    }
  });
  return Array.from(fragment.childNodes).map((node) => {
    if (node.nodeName === 'P' && !node.querySelector('img')) {
      const children = Array.from(node.childNodes).filter(
        (child) => child.nodeType !== 3 || child.textContent.trim(),
      );
      const only = children.length === 1 ? children[0] : null;
      const raw = only?.nodeName === 'A' ? only.getAttribute('href') : node.textContent.trim();
      const media = parseFeedMedia(raw || '');
      if (media) return { media };
    }
    const container = document.createElement('div');
    container.appendChild(node.cloneNode(true));
    return { html: container.innerHTML };
  });
}

export default function FeedBody({ html }) {
  const blocks = useMemo(() => feedBlocks(html), [html]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    setActive(null);
    const stop = () => {
      if (document.hidden) setActive(null);
    };
    document.addEventListener('visibilitychange', stop);
    return () => document.removeEventListener('visibilitychange', stop);
  }, [html]);
  return (
    <Box
      sx={{
        fontSize: 14,
        lineHeight: 1.6,
        '& *': { lineHeight: 1.6 },
        color: 'text.secondary',
        overflowWrap: 'anywhere',
        '& p': { m: 0 },
        '& img': { display: 'block', maxWidth: '100%', height: 'auto', m: 0 },
        '& a': { color: '#F16438' },
        '& .ql-size-small': { fontSize: 10.5 },
        '& .ql-size-large': { fontSize: 21 },
        '& .ql-size-huge': { fontSize: 35 },
        '& .ql-align-center': { textAlign: 'center' },
        '& .ql-align-right': { textAlign: 'right' },
        '& .ql-align-justify': { textAlign: 'justify' },
        '& h1': { fontSize: 24, mt: '22px', mb: '10px', color: 'text.primary' },
        '& h2': { fontSize: 21, mt: '20px', mb: '9px', color: 'text.primary' },
        '& h3': { fontSize: 18, mt: '18px', mb: '8px', color: 'text.primary' },
        '& blockquote': { borderLeft: '3px solid #F16438', pl: '12px', mx: 0 },
        '& pre': { whiteSpace: 'pre-wrap' },
        '& table': { maxWidth: '100%', display: 'block', overflowX: 'auto' },
      }}
    >
      {blocks.map((block, index) =>
        !block.media ? (
          <Box key={index} dangerouslySetInnerHTML={{ __html: block.html }} />
        ) : (
          <Stack key={index} spacing={1.5} sx={{ my: 2 }}>
            {active === index ? (
              <>
                {block.media.provider === 'X' ? (
                  <FeedXMedia key={block.media.id} id={block.media.id} />
                ) : (
                  <Box
                    component="iframe"
                    title={`${block.media.provider} 미디어`}
                    src={block.media.embed}
                    sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    allow="encrypted-media; fullscreen; picture-in-picture"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    sx={{
                      width: '100%',
                      height: block.media.provider === 'YouTube' ? 'auto' : 560,
                      aspectRatio: block.media.provider === 'YouTube' ? '16 / 9' : undefined,
                      border: 0,
                    }}
                  />
                )}
                <Button onClick={() => setActive(null)}>미디어 닫기</Button>
              </>
            ) : (
              <Button
                variant="outlined"
                onClick={() => setActive(index)}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  textAlign: 'left',
                  display: 'block',
                  color: 'text.primary',
                  borderColor: 'divider',
                }}
              >
                <Typography component="span" display="block" fontWeight={600}>
                  {block.media.provider} · 미디어 불러오기
                </Typography>
                <Typography
                  component="span"
                  display="block"
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  누르면 이 화면에서 불러옵니다. 자동 재생하지 않아요.
                </Typography>
              </Button>
            )}
            <Link href={block.media.url} target="_blank" rel="noopener noreferrer">
              {block.media.provider} 원문 열기
            </Link>
          </Stack>
        ),
      )}
    </Box>
  );
}
